import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import { useMyTeams } from '../nav/useMyTeams';
import { recommendedDocsApiRef } from '../recommendedDocs/recommendedDocsApi';
import type { RecommendedDocEntry, TeamRecommendedDocs } from '../recommendedDocs/recommendedDocsApi/types';

export type RecommendedDocs = {
  company: RecommendedDocEntry[];
  team: RecommendedDocEntry[];
  loading: boolean;
  error?: Error;
};

/*
 * Company is one curated, ordered list. Team is every team the signed-in user
 * belongs to, each with its own curated list — concatenated in team-title
 * order and deduped by path, since the caps live server-side (6 company, 3
 * per team) and there's no client-side truncation to apply on top. Each entry
 * carries its own title/description, so there's no catalog lookup to resolve.
 */
export const useRecommendedDocs = (): RecommendedDocs => {
  const recommendedDocsApi = useApi(recommendedDocsApiRef);
  const { teams, loading: teamsLoading } = useMyTeams();

  const {
    value: recommended,
    loading: recommendedLoading,
    error,
  } = useAsync(async () => {
    if (teamsLoading) {
      return {
        company: [] as RecommendedDocEntry[],
        teamsByRef: {} as TeamRecommendedDocs,
      };
    }
    const [company, teamsByRef] = await Promise.all([
      recommendedDocsApi.getCompany(),
      recommendedDocsApi.getTeams(teams.map(team => team.ref)),
    ]);
    return { company, teamsByRef };
  }, [recommendedDocsApi, teamsLoading, teams]);

  const teamRefsByTitle = [...teams].sort((a, b) => a.title.localeCompare(b.title));
  const team: RecommendedDocEntry[] = [];
  const seen = new Set<string>();
  for (const { ref } of teamRefsByTitle) {
    for (const entry of recommended?.teamsByRef[ref] ?? []) {
      if (seen.has(entry.path)) continue;
      team.push(entry);
      seen.add(entry.path);
    }
  }

  return {
    company: recommended?.company ?? [],
    team,
    loading: teamsLoading || recommendedLoading,
    error,
  };
};
